import type { CustomAlertFieldDef, FlowAlertRule } from './flowLiquidityAlertUtils';

export const FLOW_ALERT_SETTINGS_KEY = 'alertSettings';

export interface FlowAlertSettings {
  rules: FlowAlertRule[];
  custom_fields?: CustomAlertFieldDef[];
  watchlist_symbols?: string[];
  watchlistOnlyAlerts?: boolean;
  soundEnabled: boolean;
  notificationEnabled: boolean;
  soundFile: string;
  volume: number;
  cooldownMinutes: number;
  newOpportunitiesOnly: boolean;
  alertOnStableFetch?: boolean;
  minPremiumFloor?: number;
  minVolumeFloor?: number;
  useBigFlowFloor?: boolean;
  default_source?: string;
  questdb_default_window_minutes?: number;
}

export const DEFAULT_FLOW_ALERT_SETTINGS: FlowAlertSettings = {
  rules: [],
  watchlist_symbols: [],
  watchlistOnlyAlerts: false,
  soundEnabled: false,
  notificationEnabled: false,
  soundFile: 'bell-notification-337658.mp3',
  volume: 80,
  cooldownMinutes: 5,
  newOpportunitiesOnly: false,
  alertOnStableFetch: false,
  minPremiumFloor: 250_000,
  minVolumeFloor: 0,
  useBigFlowFloor: true,
};

export const FLOW_ALERT_SETTINGS_CHANGED = 'mmec-flow-alert-settings-changed';

/** Partial patch from AI chatbot or API (`alert_config`). */
export type FlowAlertAiPatch = Partial<FlowAlertSettings> & {
  rules?: FlowAlertRule[];
  custom_fields?: CustomAlertFieldDef[];
  /** Replace all rules instead of merging */
  replace_rules?: boolean;
  clear_rules?: boolean;
  replace_custom_fields?: boolean;
  clear_custom_fields?: boolean;
  preview_questdb?: boolean;
  questdb_filters?: Record<string, unknown>;
};

export function loadFlowAlertSettings(): FlowAlertSettings {
  try {
    const raw = localStorage.getItem(FLOW_ALERT_SETTINGS_KEY);
    if (!raw) return { ...DEFAULT_FLOW_ALERT_SETTINGS };
    const parsed = JSON.parse(raw);
    return {
      ...DEFAULT_FLOW_ALERT_SETTINGS,
      ...parsed,
      rules: Array.isArray(parsed.rules) ? parsed.rules : [],
      custom_fields: Array.isArray(parsed.custom_fields) ? parsed.custom_fields : [],
      watchlist_symbols: Array.isArray(parsed.watchlist_symbols)
        ? parsed.watchlist_symbols
        : [],
    };
  } catch {
    return { ...DEFAULT_FLOW_ALERT_SETTINGS };
  }
}

export function saveFlowAlertSettings(settings: FlowAlertSettings): void {
  localStorage.setItem(FLOW_ALERT_SETTINGS_KEY, JSON.stringify(settings));
  window.dispatchEvent(
    new CustomEvent(FLOW_ALERT_SETTINGS_CHANGED, { detail: settings }),
  );
}

function normalizeRule(raw: Record<string, unknown>, index: number): FlowAlertRule | null {
  const field = String(raw.field || '').trim();
  if (!field) return null;
  const op = String(raw.operator || '>').trim() as FlowAlertRule['operator'];
  const value = raw.value;
  if (value === undefined || value === '') return null;
  return {
    id: String(raw.id || `ai-${Date.now()}-${index}`),
    field,
    operator: op,
    value: typeof value === 'number' ? value : String(value),
    enabled: raw.enabled !== false,
  };
}

/**
 * Apply AI / API alert configuration onto stored settings.
 */
export function applyFlowAlertAiPatch(patch: FlowAlertAiPatch): FlowAlertSettings {
  const current = loadFlowAlertSettings();
  let rules = [...current.rules];

  if (patch.clear_rules) {
    rules = [];
  } else if (patch.rules?.length) {
    const normalized = patch.rules
      .map((r, i) => normalizeRule(r as unknown as Record<string, unknown>, i))
      .filter((r): r is FlowAlertRule => r != null);
    rules = patch.replace_rules ? normalized : [...rules, ...normalized];
  }

  let custom_fields = [...(current.custom_fields || [])];
  if (patch.clear_custom_fields) {
    custom_fields = [];
  } else if (patch.custom_fields?.length) {
    custom_fields = patch.replace_custom_fields
      ? patch.custom_fields
      : [...custom_fields, ...patch.custom_fields];
  }

  const next: FlowAlertSettings = {
    ...current,
    ...patch,
    rules,
    custom_fields,
  };
  delete (next as FlowAlertAiPatch).replace_rules;
  delete (next as FlowAlertAiPatch).clear_rules;
  delete (next as FlowAlertAiPatch).replace_custom_fields;
  delete (next as FlowAlertAiPatch).clear_custom_fields;
  delete (next as FlowAlertAiPatch).preview_questdb;
  delete (next as FlowAlertAiPatch).questdb_filters;

  saveFlowAlertSettings(next);
  return next;
}

export function summarizeFlowAlertSettings(
  settings: FlowAlertSettings,
  language: 'en' | 'ar' = 'en',
): string {
  const ar = language === 'ar';
  const parts: string[] = [];
  if (settings.notificationEnabled) {
    parts.push(ar ? 'إشعارات المتصفح: مفعّلة' : 'Browser notifications: on');
  } else {
    parts.push(ar ? 'إشعارات المتصفح: متوقفة' : 'Browser notifications: off');
  }
  if (settings.soundEnabled) {
    parts.push(ar ? 'الصوت: مفعّل' : 'Sound: on');
  }
  if (settings.alertOnStableFetch !== false) {
    parts.push(
      ar
        ? `بعد جلب Stable — حد أدنى للعلاوة $${(settings.minPremiumFloor ?? 0).toLocaleString()}`
        : `After stable fetch — min premium $${(settings.minPremiumFloor ?? 0).toLocaleString()}`,
    );
  }
  const enabledRules = settings.rules.filter((r) => r.enabled);
  if (enabledRules.length) {
    const ruleStr = enabledRules
      .slice(0, 5)
      .map((r) => `${r.field} ${r.operator} ${r.value}`)
      .join('; ');
    parts.push(ar ? `قواعد: ${ruleStr}` : `Rules: ${ruleStr}`);
  } else if (settings.useBigFlowFloor !== false) {
    parts.push(ar ? 'وضع التدفق الكبير (بدون قواعد مخصصة)' : 'Big-flow floor (no custom rules)');
  }
  const custom = settings.custom_fields?.filter((c) => c.id) || [];
  if (custom.length) {
    parts.push(
      ar
        ? `معاملات مخصصة: ${custom.map((c) => c.label || c.id).join(', ')}`
        : `Custom: ${custom.map((c) => c.label || c.id).join(', ')}`,
    );
  }
  return parts.join(' · ');
}
